import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Eye, EyeOff, Star, Loader2 } from "lucide-react";

const emptyAvis = {
  prenom: "", ville: "", note: 5, commentaire: "",
  type_client: "particulier", service: "", is_visible: true,
};

export default function AvisPage() {
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAvis, setEditAvis] = useState(null);
  const [editData, setEditData] = useState({});
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);

  const fetchAvis = async () => {
    try {
      const res = await api.get(`/avis?show_all=true`);
      setAvis(res.data);
    } catch (err) {
      toast.error("Erreur chargement des avis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvis();
    api.get(`/services`).then((res) => setServices(res.data));
  }, []);

  const handleAdd = () => {
    setIsNew(true);
    setEditAvis({});
    setEditData({ ...emptyAvis });
  };

  const handleEdit = (a) => {
    setIsNew(false);
    setEditAvis(a);
    setEditData({ ...a });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post(`/avis`, editData);
        toast.success("Avis cree");
      } else {
        await api.put(`/avis/${editAvis.id}`, editData);
        toast.success("Avis mis a jour");
      }
      setEditAvis(null);
      fetchAvis();
    } catch (err) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Supprimer l'avis de ${a.prenom} ?`)) return;
    try {
      await api.delete(`/avis/${a.id}`);
      toast.success("Avis supprime");
      fetchAvis();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleVisibility = async (a) => {
    try {
      await api.put(`/avis/${a.id}`, { is_visible: !a.is_visible });
      fetchAvis();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const updateField = (field, value) => setEditData((p) => ({ ...p, [field]: value }));

  const renderStars = (n) =>
    Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={14} className={i < n ? "fill-amber-400 text-amber-400" : "text-slate-200"} />
    ));

  return (
    <div className="space-y-6" data-testid="avis-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Avis</h1>
          <p className="text-sm text-slate-500 mt-1">{avis.length} avis au total</p>
        </div>
        <Button onClick={handleAdd} className="gap-2 bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="add-avis-button">
          <Plus className="w-4 h-4" />
          Ajouter un avis
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#336DBF]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Prenom</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Ville</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Note</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Visible</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {avis.map((a) => (
                  <TableRow key={a.id} className="hover:bg-slate-50/50" data-testid={`avis-row-${a.id}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{a.prenom}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.ville}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.service}</TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">{renderStars(a.note)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">{a.type_client}</Badge>
                    </TableCell>
                    <TableCell>
                      <button onClick={() => toggleVisibility(a)} className="p-1 rounded hover:bg-slate-100" data-testid={`toggle-avis-${a.id}`}>
                        {a.is_visible ? <Eye size={16} className="text-emerald-500" /> : <EyeOff size={16} className="text-slate-400" />}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(a)} data-testid={`edit-avis-${a.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(a)} data-testid={`delete-avis-${a.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={editAvis !== null} onOpenChange={() => setEditAvis(null)}>
        <DialogContent className="max-w-md" data-testid="edit-avis-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{isNew ? "Nouvel avis" : "Modifier l'avis"}</DialogTitle>
            <DialogDescription>{isNew ? "Ajouter un nouvel avis client" : `Avis de ${editData.prenom}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Prenom</label>
                <Input value={editData.prenom || ""} onChange={(e) => updateField("prenom", e.target.value)} data-testid="avis-prenom" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Ville</label>
                <Input value={editData.ville || ""} onChange={(e) => updateField("ville", e.target.value)} data-testid="avis-ville" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Note</label>
                <Select value={String(editData.note || 5)} onValueChange={(v) => updateField("note", parseInt(v))}>
                  <SelectTrigger data-testid="avis-note"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 étoiles</SelectItem>
                    <SelectItem value="4">4 étoiles</SelectItem>
                    <SelectItem value="3">3 étoiles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
                <Select value={editData.type_client || "particulier"} onValueChange={(v) => updateField("type_client", v)}>
                  <SelectTrigger data-testid="avis-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="particulier">Particulier</SelectItem>
                    <SelectItem value="entreprise">Entreprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Service</label>
              <Select value={editData.service || ""} onValueChange={(v) => updateField("service", v)}>
                <SelectTrigger data-testid="avis-service"><SelectValue placeholder="Choisir un service" /></SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.slug} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Commentaire</label>
              <Textarea
                value={editData.commentaire || ""}
                onChange={(e) => updateField("commentaire", e.target.value)}
                rows={4}
                data-testid="avis-commentaire"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAvis(null)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#336DBF] hover:bg-[#2A5A9E]" data-testid="save-avis-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
